import type SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSelector } from '@verclam/synapse';
import { Button, Dropdown } from '@verclam/ui-kit';

type Props = {
	readonly grants: SubjectTypeGrantsFormData;
	readonly onChange: (update: (grants: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData) => void;
	/** The active roles of the account. */
	readonly roles: readonly RoleView[];
	readonly isReadOnly: boolean;
};

/** Grants a role: pick one of the active roles not yet listed, a level, then add. */
const AddRoleGrant: React.FC<Props> = ({ grants, onChange, roles, isReadOnly }) => {
	const accessLevels = useSelector((state) => state.subjectTypeDefinitionsStore.accessLevels);
	const [roleId, setRoleId] = React.useState<number | null>(null);
	const [pickedLevel, setPickedLevel] = React.useState<string | null>(null);
	const level = pickedLevel ?? accessLevels?.[0]?.value ?? null;

	const availableRoles = React.useMemo(
		() => roles.filter((role) => role.isActive && !grants.hasGrant('role', role.id)),
		[roles, grants],
	);

	const handleAddClick = React.useCallback(() => {
		if (roleId === null || level === null) {
			return;
		}

		onChange((current) => current.addGrant('role', roleId, level));
		setRoleId(null);
	}, [onChange, roleId, level]);

	return (
		<Grid columns={12} columnGap={1} alignItems="center">
			<GridItem columnSize={{ xs: 12, xl: 6 }}>
				<Dropdown
					name="grantRole"
					label="Rôle"
					isLabelHidden
					placeholder="Rôle…"
					options={availableRoles}
					optionLabel="label"
					optionValue="id"
					value={roleId}
					disabled={isReadOnly}
					onChange={setRoleId}
				/>
			</GridItem>
			<GridItem columnSize={{ xs: 8, xl: 4 }}>
				<Dropdown
					name="grantRoleLevel"
					label="Niveau d'accès du rôle"
					isLabelHidden
					options={accessLevels ?? []}
					optionLabel="label"
					optionValue="value"
					value={level}
					disabled={isReadOnly}
					onChange={setPickedLevel}
				/>
			</GridItem>
			<GridItem columnSize={{ xs: 4, xl: 2 }}>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label="Ajouter"
					disabled={isReadOnly || roleId === null}
					onClick={handleAddClick}
				/>
			</GridItem>
		</Grid>
	);
};

export default AddRoleGrant;
